import { CensusVariables } from '@/constants/territories/census-variables';
import { POLYGON_COLORS } from '@/constants/territories/colors';
import { SAMPLE_PREFERRED_PERSONS } from '@/constants/territories/dummy';
import { AreaInfo } from '@/types/territories/area.info.type';
import { Territory } from '@/types/territories/territory.type';
import { ZorTerritorySettings } from '@/types/zor.territory-settings.type';

export interface AggregatedStats {
  state: string;
  zctaCodes: string[];
  totalPopulation: number;
  totalMedianAge: number;
  totalBirthRate: number;
  totalMedianHomeValue: number;
  totalMedianHouseholdIncome: number;
  totalPopOver40: number;
}

export const calculateAggregatedStats = (
  areaInfos: AreaInfo[]
): AggregatedStats => {
  const initialStats: AggregatedStats = {
    state: areaInfos[0].state,
    zctaCodes: areaInfos.map((area) => area.zcta_code),
    totalPopulation: 0,
    totalMedianAge: 0,
    totalBirthRate: 0,
    totalMedianHomeValue: 0,
    totalMedianHouseholdIncome: 0,
    totalPopOver40: 0,
  };

  const resultStats = areaInfos.reduce((stats, area) => {
    const population = area.population;
    stats.totalPopulation += population;
    stats.totalMedianAge += area.median_age * population;
    stats.totalBirthRate += area.birth_rate * population;
    stats.totalMedianHomeValue += area.median_home_value * population;
    stats.totalMedianHouseholdIncome +=
      area.median_household_income * population;
    stats.totalPopOver40 += area.pop_over_40;
    return stats;
  }, initialStats);

  return {
    state: resultStats.state,
    zctaCodes: resultStats.zctaCodes,
    totalPopulation: resultStats.totalPopulation,
    totalMedianAge: resultStats.totalMedianAge / resultStats.totalPopulation,
    totalBirthRate: resultStats.totalBirthRate / resultStats.totalPopulation,
    totalMedianHomeValue:
      resultStats.totalMedianHomeValue / resultStats.totalPopulation,
    totalMedianHouseholdIncome:
      resultStats.totalMedianHouseholdIncome / resultStats.totalPopulation,
    totalPopOver40: resultStats.totalPopOver40,
  };
};

export const getTerritoryColor = (
  territory: Territory,
  territorySettings: ZorTerritorySettings | undefined
): string => {
  const status =
    territory.status === 'AVAILABLE' && territory.is_liked
      ? 'LIKED'
      : territory.status;
  const territoryColor = territorySettings?.colors
    ? territorySettings?.colors[
        status.toLowerCase() as keyof typeof territorySettings.colors
      ]
    : POLYGON_COLORS[status];

  return territoryColor;
};
export const isDecimal = (code: string): boolean => {
  return Object.values(CensusVariables).some(
    (variable) =>
      variable.code === code &&
      !variable.label.toLowerCase().includes('income') &&
      !variable.label.toLowerCase().includes('value')
  );
};

export const getStateUserCount = (
  stateCode: string,
  preferenceCount: number
): number => {
  const sampleCount =
    SAMPLE_PREFERRED_PERSONS[
      stateCode as keyof typeof SAMPLE_PREFERRED_PERSONS
    ] ?? 0;
  return sampleCount + preferenceCount;
};
